import { Timebar } from './index';
import { container } from '@graphistry/falcor-react-redux';
import { timeDurations, timeAggregations } from '@graphistry/viz/src/models/timebar';
import {
    updateTimebarMode,
    updateTimebarEnableTimestamp,
    updateTimebarSpeed,
    updateTimebarPlay,
    updateTimebarLoop,
    updateTimebarPlayheadStart,
    updateTimebarInterval
} from '../../actions/timebar';

export function mapTimebarFragment(timebar = {}, props = {}, $falcor) {
    const aggregations = timebar.aggregations || [];
    const [details, summary, prevDetails, prevSummary] = getTimeAggregations(timebar, props);
    const { level: detailsLevel } = (details || prevDetails || {});
    const { level: summaryLevel } = (summary || prevSummary || {});

    // Handle phenomena around having only 1 or 2 global bins
    const globalBoundsPadding = Math.max(5, Math.round((timebar.maxValue - timebar.minValue) * 0.05));

    return {
        $falcor,
        ...timebar,
        detailsLevel,
        minTime: timebar.minValue - globalBoundsPadding,
        maxTime: timebar.maxValue + globalBoundsPadding,
        details: aggregations[detailsLevel] || prevDetails,
        summary: aggregations[summaryLevel] || prevSummary,
    };
}

export const withTimebarMetadata = container({
    renderLoading: true,
    mapFragment: mapTimebarFragment,
    fragment(timebar = {}, props = {}) {

        const query = `
            id, name, identifier,
            dataType, componentType,
            filter: { enabled }, range,
            binType, minValue, maxValue, timebar: { mode, enableTimestamp, speed, play, loop, playheadStart, interval }`;

        let aggregationLevels = timebar.aggregationLevels || [];
        if (timebar && timebar.minValue != null && timebar.maxValue != null && props.width && props.zoomScale) {
            aggregationLevels = timebar.aggregationLevels =
                getTimeAggregations(timebar, props).map(({ level }) => `${level}`);
        }

        if (aggregationLevels.length > 0) {
            return `{
                ${query},
                aggregations: {
                    ${JSON.stringify(aggregationLevels)}: {
                        binWidth, numBins,
                        global: { min, max, binCounts, binValues },
                        masked: { min, max, binCounts, binValues },
                        filtered: { min, max, binCounts, binValues }
                    }
                }
            }`;
        }
        return `{ ${query} }`;
    },
    dispatchers: {
        onUpdateTimebarMode: updateTimebarMode,
        onUpdateTimebarEnableTimestamp: updateTimebarEnableTimestamp,
        onUpdateTimebarSpeed: updateTimebarSpeed,
        onUpdateTimebarPlay: updateTimebarPlay,
        onUpdateTimebarLoop: updateTimebarLoop,
        onUpdateTimebarPlayheadStart: updateTimebarPlayheadStart,
        onUpdateTimebarInterval: updateTimebarInterval
    }
});

function getTimeAggregations(timebar, props) {
    const details = getTimebarDetails(timebar, props);
    const summary = findPreviousAggregation(timebar, props, details.level, selectSummaryAggregation(timebar, props, details.level));
    const prevDetails = findPreviousAggregation(timebar, props, details.level, (agg, level) => ((agg.numBins || -1) > 0) ? agg : null);
    const prevSummary = findPreviousAggregation(timebar, props, summary.level, selectSummaryAggregation(timebar, props, summary.level));
    return [details, summary, prevDetails, prevSummary];
}

function getTimebarDetails(timebar, props) {
    const { minValue = 0, maxValue = 0, aggregations = {} } = timebar;
    const detailsLevel = getDetailAggregationLevel(minValue, maxValue, props);
    return { ...aggregations[detailsLevel], name: 'details', level: detailsLevel };
}

function findPreviousAggregation(timebar, props, startAggLevel, selector) {
    const { aggregations = {} } = timebar;
    let agg, lastAggLevel = timeDurations.length - 1;
    let aggLevel = typeof startAggLevel === 'number' ? startAggLevel : lastAggLevel;
    while (aggLevel < lastAggLevel) {
        ++aggLevel;
        if (agg = selector(aggregations[aggLevel] || {}, aggLevel, aggLevel === lastAggLevel)) {
            return { level: aggLevel, ...agg };
        }
    }
    return { ...aggregations[aggLevel], level: aggLevel };
}

function selectSummaryAggregation(timebar, props, detailsLevel, minSummaryBarWidth = 10) {
    // const { minValue = 0, maxValue = 0 } = timebar;
    const maxNumSummaryBins = (props.width || 0) / minSummaryBarWidth;
    return function selectSummary(agg, level, isLastLevel) {
        const { numBins = -1 } = agg;
        if (numBins !== -1 && numBins <= maxNumSummaryBins) {
            return agg;
        }
        if (isLastLevel) {
            return findPreviousAggregation(timebar, props, detailsLevel, (agg, level) => {
                if (level > 1 && timeDurations[level][0] === 1) {
                    return agg;
                }
            });
        }
    }
}

function getDetailAggregationLevel(minTime, maxTime, props, idealDetailsBinWidth = 50) {
    const idealNumDetailsBins = Math.floor((props.width || 0) / idealDetailsBinWidth) || 1;
    if (props.zoomScale) {
        const [beginTime, endTime] = props.zoomScale.domain();
        return getTimeAggregationLevelForInterval(Math.abs(+endTime - +beginTime) / idealNumDetailsBins);
    }
    return getTimeAggregationLevelForInterval(Math.abs(+maxTime - +minTime) / idealNumDetailsBins);
}

function getTimeAggregationLevelForInterval(timeInterval) {
    const timeAggregation = timeDurations.indexOf(timeAggregations(timeInterval));
    if (timeAggregation === 0) return timeAggregation;
    if (timeAggregation === -1) return timeDurations.length - 1;
    return Math.max(0, Math.min(timeDurations.length - 1, timeAggregation - 1));
}
